import {DataFetcherRequest} from '@aboutyou/data-fetcher';
import {useBasketTokenAwaitedAnySession} from '@aboutyou/user';
import {useCallback, useEffect, useRef} from 'react';

/**
 * If the session changed, we don't want to show a loading indicator to the user, so we always keep the previous.
 * But in case any other parameter changes, we want to show a loading indicator when the request takes a while.
 * This should prevent flickering with fast network requests, but indicate that something is happening in case it takes longer.
 */
export const useKeepPreviousData = () => {
  /**
   * This hook is necessary to ensure we wait for the basket token.
   * When using the useBasketTokenAwaited* hook, the session in this hook resolves faster
   * than in the request hook. Without this synchronization, keepPreviousData may return
   * incorrect results, causing skeletons to display when the session changes.
   */
  const {session} = useBasketTokenAwaitedAnySession();
  const currentSession =
    (session.type.customer?.authToken || session.type.guest?.sessionId) ?? '';
  const lastSessionBeforeLoadingStateToggle = useRef(currentSession);

  const keepPreviousData =
    lastSessionBeforeLoadingStateToggle.current === currentSession
      ? ('keep-until-frustrating' as const)
      : true;

  return {
    keepPreviousData,
    resetOnFetchEnd: useCallback(() => {
      lastSessionBeforeLoadingStateToggle.current = currentSession;
    }, [currentSession]),
  };
};

export const useResetOnFetchEnd = <Data extends object>(
  request: DataFetcherRequest<Data>,
  resetOnFetchEnd: VoidFunction,
) => {
  useEffect(() => {
    if (!request.isFetching) {
      resetOnFetchEnd();
    }
  }, [request.isFetching, resetOnFetchEnd]);
};
